import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent
} from '@progress/kendo-react-grid';
import { useContext, useEffect, useState } from 'react';
import { DataResult, State, process } from '@progress/kendo-data-query';

import {
  GridMode,
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../grid/grid-tool-bar';
import Popup from '../../popup-window';
import AnswerColumn from './answer-column';
import { DetailPlayer } from '../detail-player';
import { TaskContentAnswerEntity } from '../../../types';
import {
  GetForm,
  GetTaskContentById
} from '../../../utils/game-document/assets/task-contents';
import { ManualScoring } from '../../../types/manual-scoring';
import { roundingDecimalNumber } from '../../../utils/number';
import { PlayerContextState } from '../../../contexts/player';
import { capitalizeFirstLetter } from '../../../utils/string';
import {
  GetTaskById,
  GetTaskTitleByEntity
} from '../../../utils/game-document/assets';
import { GameDocumentContext } from '../../../contexts/game-document';
import { DefaultGridSettings } from '../../../constants/grid-settings';
import { GetResourceValue } from '../../../utils/game-document/resources';
import { GetTaskContentByTaskId } from '../../../utils/game-engine/player';

export interface TaskState {
  taskName?: string;
  question?: string;
  playerCode?: string;
  playerName?: string;
  questionType?: string;
  answer?: string;
  scoringType?: string;
  time?: string;
  score?: number;
  taskId?: string;
  taskContentId?: string;
  formId?: string;
  answers?: TaskContentAnswerEntity;
}

interface TaskListProps {
  type: 'Task' | 'OnDemandTask';
  playerStates?: PlayerContextState[];
  onViewChange?: (view: GridMode) => void;
  viewMode?: GridMode;
  onSubmitScore?: (e: ManualScoring) => void;
  onExport?: () => void;
}

export const TaskList = ({ type, playerStates, ...props }: TaskListProps) => {
  const initialDataState: State = {
    sort: [{ field: 'playerName', dir: 'asc' }],
    ...DefaultGridSettings.initialDataState
  };
  const [gameDocument] = useContext(GameDocumentContext);
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [dataResult, setDataResult] = useState<DataResult>();
  const [data, setData] = useState<TaskState[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<
    ManualScoring | undefined
  >();

  const taskList = (gameDocument.gameDocument?.assets.tasks ?? []).map(
    (t) => t.id
  );

  const generateTasks = () => {
    let tasks: TaskState[] = [];

    if (playerStates) {
      let data = playerStates?.map((playerState) => {
        try {
          const filteredTasks = (playerState?.playerState!.tasks ?? []).filter(
            (t) =>
              type === 'Task'
                ? taskList.includes(t.id ?? '')
                : !taskList.includes(t.id ?? '')
          );
          return filteredTasks.map((task) => {
            const taskById = GetTaskById(
              gameDocument.gameDocument,
              task.id ?? ''
            );

            // handle task form base
            if (
              task.taskContentFormAnswers !== undefined &&
              task.taskContentFormAnswers.length > 0
            ) {
              return task?.taskContentFormAnswers?.map((taskContentAnswer) => {
                const taskContentById = GetTaskContentById(
                  gameDocument.gameDocument,
                  task.id ?? ''
                );
                let taskName = '';
                let question = '';
                let questionType = '';
                let scoringType = '';
                let taskContentId = '';

                if (
                  (taskById || taskContentById) &&
                  gameDocument.gameDocument
                ) {
                  const form = GetForm(
                    gameDocument.gameDocument,
                    (taskById ? taskById.taskContentId : taskContentById?.id) ??
                      '',
                    taskContentAnswer.formId || ''
                  );
                  if (form) {
                    taskName = GetTaskTitleByEntity(
                      gameDocument.gameDocument,
                      taskById,
                      taskContentById
                    );
                    question = GetResourceValue(
                      gameDocument.gameDocument,
                      form.questionResId || '',
                      ''
                    );
                    questionType = capitalizeFirstLetter(form.type || '');
                    scoringType = form.isManualScoring ? 'M' : 'A';
                    taskContentId =
                      (taskById
                        ? taskById.taskContentId
                        : taskContentById?.id) ?? '';
                  }
                }

                return {
                  taskId: task?.id,
                  taskName,
                  question,
                  playerName: playerState?.playerState?.name,
                  playerCode: playerState?.playerState?.code,
                  questionType,
                  scoringType,
                  formId: taskContentAnswer.formId,
                  score: parseInt(taskContentAnswer?.score?.toString() ?? '0'),
                  taskContentId,
                  answers: taskContentAnswer
                };
              });
            }

            // handle task AR base
            if (task.dataARMiniGame) {
              const selectedTaskContent =
                type === 'Task'
                  ? GetTaskContentByTaskId(
                      gameDocument.gameDocument!,
                      task.id ?? ''
                    )
                  : GetTaskContentById(
                      gameDocument.gameDocument,
                      task.id ?? ''
                    );

              let taskName = '';
              let question = '';
              let questionType = '';
              let scoringType = '';
              let taskContentId = '';
              let answers = '';

              if (
                (taskById || selectedTaskContent) &&
                gameDocument.gameDocument
              ) {
                taskName = GetTaskTitleByEntity(
                  gameDocument.gameDocument,
                  taskById,
                  selectedTaskContent
                );
                scoringType = selectedTaskContent?.ARMiniGameSetting
                  ?.manualScoring
                  ? 'M'
                  : 'A';
                questionType = `${selectedTaskContent?.ARMiniGame} (AR)`;
                answers = task.dataARMiniGame;
                taskContentId =
                  (taskById
                    ? taskById.taskContentId
                    : selectedTaskContent?.id) ?? '';
              }

              return {
                taskId: task?.id,
                taskType: type,
                taskName,
                question,
                playerName: playerState?.playerState?.name,
                playerCode: playerState?.playerState?.code,
                questionType,
                scoringType,
                formId: task.id,
                score: task.dataARMiniGame.score ?? 0,
                taskContentId,
                answers
              };
            }
          });
        } catch (error) {}
      });

      data?.forEach((dataTasks) => {
        if (dataTasks) {
          dataTasks?.forEach((item) => {
            item !== undefined && tasks.push(item as TaskState);
          });
        }
      });
      setData(tasks.flat());
    }
  };

  const ScoringTypeColoring = (props: GridCellProps) => {
    return (
      <td>
        <span className={'text-success'}>
          {props?.dataItem?.scoringType ?? '-'}
        </span>
      </td>
    );
  };

  const ScoreColumn = (props: GridCellProps) => {
    return (
      <td>
        <span>{roundingDecimalNumber(props?.dataItem?.score ?? 0, 2)}</span>
      </td>
    );
  };

  const CommandColumn = (props: GridCellProps) => {
    return (
      <td>
        <div
          className={'d-flex align-items-center justify-content-center w-full'}>
          <span
            onClick={() => onView(props)}
            className={'material-symbols-outlined cursor-pointer text-dark'}>
            visibility
          </span>
        </div>
      </td>
    );
  };

  const onView = (props: GridCellProps) => {
    const answerSelected: ManualScoring = {
      playerName: props?.dataItem?.playerName,
      gameCode: gameDocument?.gameCode,
      playerCode: props?.dataItem?.playerCode,
      taskId: props?.dataItem?.taskId,
      formId: props?.dataItem?.formId,
      answer: JSON.stringify(props?.dataItem?.answers),
      isScored:
        (props?.dataItem?.scoringType === 'A' ? true : false) ||
        (props?.dataItem?.score ?? 0) > 0,
      score: props?.dataItem?.score
    };
    setSelectedAnswer(answerSelected);
  };

  useEffect(() => {
    generateTasks();
  }, [type, playerStates]);

  useEffect(() => {
    setDataResult(process(data, dataState));
  }, [data, dataState]);

  const QuestionType = (props: GridCellProps) => {
    return (
      <td>
        {props.dataItem?.questionType === 'Radio'
          ? 'Multiple Choice'
          : props.dataItem?.questionType}
      </td>
    );
  };

  return (
    <div
      className={
        'd-flex flex-column justify-items-center align-items-center w-100'
      }
      style={{
        flexGrow: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}>
      <div
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%'
        }}>
        <GridToolBar
          viewMode={props.viewMode}
          searchPlaceholder={'Search'}
          columnsToSearch={[
            'playerName',
            'taskName',
            'question',
            'questionType',
            'score'
          ]}
          onGridModeChange={(e) => {
            if (props.onViewChange) {
              props.onViewChange(e.gridMode);
            }
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
          handleExport={() => {
            if (props.onExport) {
              props.onExport();
            }
          }}
        />

        <Grid
          style={{
            flexGrow: 1,
            minHeight: 0,
            overflow: 'hidden'
          }}
          id={'grid-tasks'}
          className={'w-full'}
          pageable={DefaultGridSettings.pagerSettings}
          sortable={true}
          data={dataResult}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <Column field={'taskName'} title={'Task'} />
          <Column field={'playerName'} title={'Player'} />
          <Column title={'Question type'} cell={QuestionType} />
          <Column
            field={'scoringType'}
            cell={ScoringTypeColoring}
            title={'Type'}
          />
          <Column cell={AnswerColumn} title={'Answer'} />
          <Column cell={ScoreColumn} title={'Score'} />
          <Column width={'100px'} cell={CommandColumn} />
        </Grid>
      </div>

      {selectedAnswer && (
        <Popup
          handleClose={() => setSelectedAnswer(undefined)}
          width={'600'}
          className={
            'box assessment-export d-flex flex-column position-relative'
          }>
          <DetailPlayer
            type={type}
            dataPlayer={selectedAnswer}
            onSubmitScore={(e) => {
              if (props.onSubmitScore) {
                props.onSubmitScore(e);
                setSelectedAnswer(undefined);
              }
            }}
          />
        </Popup>
      )}
    </div>
  );
};
